import { dxtToLocalServerTime } from "@dexteel/mesf-core";
import { Box, Typography } from "@mui/material";
import { Task } from "@/controls/ganttAsset/types/public-types";
import { RECLASSIFIED_COLOR } from "@/shared/colors";
import { withFraction32 } from "@/utils/fraction";
import { GanttDelay, GanttEvent, GanttWorkOrder } from "../../models/types";

interface BundleTooltipProps {
	task: Task;
	fontSize: string;
	fontFamily: string;
}

const formatDuration = (start: Date, end: Date): string => {
	const diffMs = end.getTime() - start.getTime();
	const diffMins = Math.floor(diffMs / 60000);
	const hours = Math.floor(diffMins / 60);
	const mins = diffMins % 60;
	if (hours > 0) {
		return `${hours}h ${mins}m`;
	}
	return `${mins}m`;
};

export const BundleTooltip = ({
	task,
	fontSize,
	fontFamily,
}: BundleTooltipProps) => {
	// For taskLine (delays), show delay details
	if (task.type === "taskLine") {
		// task.source is the ProcessLine, and ProcessLine.source is the GanttDelay
		const delay = task.source?.source as GanttDelay;
		const delayCode = [delay?.DelayCodeGroup, delay?.DelayCodeName]
			.filter(Boolean)
			.join(" → ");
		return (
			<Box
				sx={{
					backgroundColor: "#fff",
					color: "#333",
					padding: 2,
					borderRadius: 1,
					minWidth: 200,
					boxShadow: 2,
					border: "1px solid #ddd",
				}}
			>
				<Typography
					variant="subtitle1"
					sx={{
						fontWeight: "bold",
						borderBottom: "1px solid #ccc",
						paddingBottom: 0.5,
						marginBottom: 1,
						color: "#d32f2f",
						fontSize: "16px",
					}}
				>
					Delay
				</Typography>

				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
					<TooltipRow
						label="Duration"
						value={formatDuration(task.start, task.end)}
					/>
					<TooltipRow
						label="From"
						value={dxtToLocalServerTime(task.start, "MM/dd HH:mm:ss")}
					/>
					<TooltipRow
						label="To"
						value={dxtToLocalServerTime(task.end, "MM/dd HH:mm:ss")}
					/>
					{delay?.EStop && <TooltipRow label="E-Stop" value={delay.EStop} />}
					<TooltipRow
						label="Delay Code"
						value={delayCode || "Not specified"}
						valueColor={delayCode ? undefined : "#d32f2f"}
					/>
					<TooltipRow label="Comments" value={delay?.DelayComment || "-"} />
				</Box>
			</Box>
		);
	}

	// For Work Order bars, show work order details. Bundle events also carry a
	// WorkOrderId, so a WO source is only one WITHOUT an EventType.
	const workOrder = task.source as Partial<GanttWorkOrder>;
	if (
		(task.source as Partial<GanttEvent>)?.EventType == null &&
		workOrder?.WorkOrderId != null
	) {
		return (
			<Box
				sx={{
					backgroundColor: "#fff",
					color: "#333",
					padding: 2,
					borderRadius: 1,
					minWidth: 200,
					boxShadow: 2,
					border: "1px solid #ddd",
				}}
			>
				<Typography
					variant="subtitle1"
					sx={{
						fontWeight: "bold",
						borderBottom: "1px solid #ccc",
						paddingBottom: 0.5,
						marginBottom: 1,
						color: "#3D5E9E",
						fontSize: "16px",
					}}
				>
					Work Order # {workOrder.WorkOrderNumber ?? "—"}
				</Typography>

				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
					<TooltipRow label="Part Number" value={workOrder.PartNumber} />
					<TooltipRow
						label="Extended Part #"
						value={workOrder.ExtendedPartNumber}
					/>
					<TooltipRow label="Quantity" value={workOrder.Quantity} />
					<TooltipRow label="Status" value={workOrder.Status} />
					<TooltipRow
						label="Start"
						value={dxtToLocalServerTime(task.start, "MM/dd HH:mm")}
					/>
					<TooltipRow
						label="End"
						value={dxtToLocalServerTime(task.end, "MM/dd HH:mm")}
					/>
					<TooltipRow
						label="Duration"
						value={formatDuration(task.start, task.end)}
					/>
				</Box>
			</Box>
		);
	}

	// For task (bundles/batches), show event details
	const event = task.source as GanttEvent;

	// Violet bottom-band segment: the reclassification itself, from → to.
	if (task.id.startsWith("reclassified-")) {
		return (
			<Box
				sx={{
					backgroundColor: "#fff",
					color: "#333",
					padding: 2,
					borderRadius: 1,
					minWidth: 200,
					boxShadow: 2,
					border: "1px solid #ddd",
				}}
			>
				<Typography
					variant="subtitle1"
					sx={{
						fontWeight: "bold",
						borderBottom: "1px solid #ccc",
						paddingBottom: 0.5,
						marginBottom: 1,
						color: RECLASSIFIED_COLOR,
						fontSize: "16px",
					}}
				>
					Reclassified Bundle {event?.BundleId}
				</Typography>

				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
					<TooltipRow label="From" value={event?.OriginalPartNumber} />
					<TooltipRow label="To" value={event?.PartNumber} />
					<TooltipRow label="Pieces" value={event?.BarCount} />
					<TooltipRow
						label="Start"
						value={dxtToLocalServerTime(task.start, "MM/dd HH:mm")}
					/>
					<TooltipRow
						label="End"
						value={dxtToLocalServerTime(task.end, "MM/dd HH:mm")}
					/>
				</Box>
			</Box>
		);
	}

	const isBatch = event?.EventType === "Batch";
	const label = isBatch ? "Batch" : "Bundle";

	return (
		<Box
			sx={{
				backgroundColor: "#fff",
				color: "#333",
				padding: 2,
				borderRadius: 1,
				minWidth: 200,
				boxShadow: 2,
				border: "1px solid #ddd",
			}}
		>
			<Typography
				variant="subtitle1"
				sx={{
					fontWeight: "bold",
					borderBottom: "1px solid #ccc",
					paddingBottom: 0.5,
					marginBottom: 1,
					color: "#2e7d32",
					fontSize: "16px",
				}}
			>
				{label}
			</Typography>

			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
				{event?.PartNumber ? (
					<TooltipRow label="Part Number" value={event.PartNumber} />
				) : null}
				{(event?.IsCutback === true ||
					event?.IsReclassified === true ||
					event?.WasReclassified === true) && (
					<TooltipRow label="Original Part" value={event?.OriginalPartNumber} />
				)}
				{!isBatch && event?.IsCutback !== true && (
					<>
						<TooltipRow
							label="Bundle # (Shift)"
							value={event?.ShiftBundleNo ?? "In progress"}
							valueColor={event?.ShiftBundleNo == null ? "#9e9e9e" : undefined}
						/>
						<TooltipRow
							label="Bundle # (Run)"
							value={
								event?.ProfileRunNo ??
								(event?.WorkOrderId == null
									? "No work order (part unknown)"
									: "In progress")
							}
							valueColor={event?.ProfileRunNo == null ? "#9e9e9e" : undefined}
						/>
						<TooltipRow
							label="Bundle # (Work Order)"
							value={
								event?.RunBundleNo ??
								(event?.WorkOrderId == null
									? "No active work order"
									: "In progress")
							}
							valueColor={event?.RunBundleNo == null ? "#9e9e9e" : undefined}
						/>
					</>
				)}
				{event?.TagNo && <TooltipRow label="Tag #" value={event.TagNo} />}
				<TooltipRow
					label="Avg Length"
					value={event?.AvgLength ? withFraction32(event.AvgLength) : null}
				/>
				<TooltipRow
					label="Start"
					value={dxtToLocalServerTime(task.start, "MM/dd HH:mm")}
				/>
				<TooltipRow
					label="End"
					value={dxtToLocalServerTime(task.end, "MM/dd HH:mm")}
				/>
				<TooltipRow
					label="Duration"
					value={formatDuration(task.start, task.end)}
				/>
				<TooltipRow label="Good Pieces" value={event?.BarCount} />
			</Box>
		</Box>
	);
};

const TooltipRow = ({
	label,
	value,
	valueColor,
}: {
	label: string;
	value: string | number | null | undefined;
	valueColor?: string;
}) => (
	<Box sx={{ display: "flex", justifyContent: "space-between", gap: 2 }}>
		<Typography sx={{ color: "#666", fontSize: "14px" }}>{label}:</Typography>
		<Typography
			sx={{ fontWeight: "bold", fontSize: "14px", color: valueColor }}
		>
			{value ?? "-"}
		</Typography>
	</Box>
);
